import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { AppLayout } from '../components/Layout/AppLayout';
import { useAudit } from '../hooks/useAudit';
import { useAuth } from '../hooks/useAuth';
import { LoadingState, ErrorState, EmptyState } from '../components/common/FeedbackStates';
import { Filter, MapPin, Loader, Eye } from 'lucide-react';
import { useMembers } from '../hooks/useMembers';
import { useCredentials } from '../hooks/useCredentials';
import { apiClient } from '../api/client';
import { caminhoDaAcao, detalheDaAcao, traduzirAcao } from '../utils/auditLabels';
import { PageHeader } from '../components/common/PageHeader';
import { Paginacao, paginar } from '../components/common/Paginacao';
import { formatarNome } from '../utils/nomes';

const LOGS_POR_PAGINA = 50;

// A3: atalhos de período. "Hoje" começa à meia-noite; os outros contam N dias para trás.
type Atalho = 'hoje' | '7' | '30';
const ATALHOS: { id: Atalho; rotulo: string }[] = [
  { id: 'hoje', rotulo: 'Hoje' },
  { id: '7', rotulo: '7 dias' },
  { id: '30', rotulo: '30 dias' },
];

interface GeoInfo {
  city?: string;
  regionName?: string;
  country?: string;
  district?: string;
  status: 'success' | 'fail' | 'loading';
}

const IPReveal = ({ ip }: { ip?: string }) => {
  const [show, setShow] = useState(false);
  if (!ip) return <span>-</span>;
  if (show) return <span style={{ fontFamily: 'monospace', fontSize: 'var(--text-xs)' }}>{ip}</span>;
  return (
    <button 
      type="button"
      onClick={() => setShow(true)}
      style={{ background: 'transparent', border: 'none', color: 'var(--color-primary-400)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px' }}
      title="Ver IP"
    >
      <Eye size={16} />
    </button>
  );
};

type AuditFilters = { user_id: string; credential_id: string; start_date: string; end_date: string };

// Formata uma data no padrão do <input type="datetime-local"> (YYYY-MM-DDTHH:mm, horário local)
const toDateTimeLocal = (date: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const inicioDoAtalho = (atalho: Atalho): Date => {
  if (atalho === 'hoje') {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    return hoje;
  }
  return new Date(Date.now() - Number(atalho) * 24 * 60 * 60 * 1000);
};

// A3: ao abrir a tela, os últimos 7 dias (a tela já abre com dados; antes era só a última hora).
const periodFilters = (atalho: Atalho, base?: AuditFilters): AuditFilters => ({
  user_id: base?.user_id ?? '',
  credential_id: base?.credential_id ?? '',
  start_date: toDateTimeLocal(inicioDoAtalho(atalho)),
  end_date: '',
});

// O input devolve horário local (ex: Brasília); o banco guarda em UTC.
// Convertendo para ISO/UTC aqui, "14:00" no filtro significa 14:00 no relógio do usuário.
const toApiFilters = (filters: AuditFilters) => ({
  ...filters,
  start_date: filters.start_date ? new Date(filters.start_date).toISOString() : '',
  end_date: filters.end_date ? new Date(filters.end_date).toISOString() : '',
});

export function AuditLog() {
  const { logs, isLoading, error, fetchLogs } = useAudit();
  const { members, fetchMembers } = useMembers();
  const { credentials, fetchCredentials } = useCredentials();
  const { membership } = useAuth();
  const isAdmin = membership?.role === 'admin' || membership?.role === 'owner';

  const [filters, setFilters] = useState<AuditFilters>(() => periodFilters('7'));
  // Atalho escolhido; some quando a pessoa edita as datas na mão.
  const [atalho, setAtalho] = useState<Atalho | null>('7');
  const [pagina, setPagina] = useState(1);

  // Cache de geolocalização por IP
  const [geoCache, setGeoCache] = useState<Record<string, GeoInfo>>({});

  useEffect(() => {
    if (isAdmin) {
      // Usa o filtro inicial (7 dias); de propósito sem `filters` nas dependências,
      // para não recarregar a cada tecla digitada — o recarregamento é pelo botão "Filtrar".
      fetchLogs(toApiFilters(filters));
      fetchMembers();
      fetchCredentials();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, fetchLogs, fetchMembers, fetchCredentials]);

  // Geolocalizar IPs únicas dos logs
  const geolocateIps = useCallback(async (ips: string[]) => {
    const uniqueIps = [...new Set(ips)].filter(ip => ip && ip !== '127.0.0.1' && ip !== '::1' && !geoCache[ip]);
    if (uniqueIps.length === 0) return;

    // Marcar como loading
    setGeoCache(prev => {
      const next = { ...prev };
      uniqueIps.forEach(ip => { next[ip] = { status: 'loading' }; });
      return next;
    });

    try {
      const data = await apiClient.post<Array<{ query: string; status: string; city?: string; regionName?: string; country?: string; district?: string }>>('/audit/geo', uniqueIps);
      setGeoCache(prev => {
        const next = { ...prev };
        data.forEach(item => {
          next[item.query] = {
            status: item.status === 'success' ? 'success' : 'fail',
            city: item.city,
            regionName: item.regionName,
            country: item.country,
            district: item.district,
          };
        });
        return next;
      });
    } catch {
      setGeoCache(prev => {
        const next = { ...prev };
        uniqueIps.forEach(ip => { next[ip] = { status: 'fail' }; });
        return next;
      });
    }
  }, [geoCache]);

  // Só a página que está na tela: com 7 dias há muitos registros, e cada IP é uma consulta externa.
  const logsDaPagina = useMemo(() => paginar(logs, pagina, LOGS_POR_PAGINA), [logs, pagina]);

  useEffect(() => {
    if (logsDaPagina.length === 0) return;
    const ips = logsDaPagina.map(l => l.ip_address).filter(Boolean) as string[];
    geolocateIps(ips);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logsDaPagina]);

  const aplicarAtalho = (escolhido: Atalho) => {
    const novos = periodFilters(escolhido, filters);
    setAtalho(escolhido);
    setFilters(novos);
    setPagina(1);
    fetchLogs(toApiFilters(novos));
  };

  const handleFilter = (e: React.FormEvent) => {
    e.preventDefault();
    setPagina(1);
    fetchLogs(toApiFilters(filters));
  };

  const handleClear = () => {
    setFilters({ user_id: '', credential_id: '', start_date: '', end_date: '' });
    setAtalho(null);
    setPagina(1);
    fetchLogs();
  };

  if (!isAdmin) {
    return (
      <AppLayout>
        <ErrorState message="Acesso negado. Apenas administradores e proprietários podem ver o registro de atividades." />
      </AppLayout>
    );
  }

  const getMemberName = (log: typeof logs[0]) => {
    const member = members.find(m => m.id === log.user_id);
    if (member) return formatarNome(member.name) || member.email;
    return formatarNome(log.metadata_log?.user_name) || log.metadata_log?.user_email || 'Usuário removido';
  };

  const getCredentialLabel = (id?: string) => {
    if (!id) return '-';
    const cred = credentials.find(c => c.id === id);
    return cred ? cred.label : 'Credencial removida';
  };

  const renderLocation = (ip?: string) => {
    if (!ip || ip === '127.0.0.1' || ip === '::1') {
      return <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>Local</span>;
    }
    const geo = geoCache[ip];
    if (!geo || geo.status === 'loading') {
      return <Loader size={12} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-text-muted)' }} />;
    }
    if (geo.status === 'fail') {
      return <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>Não localizada</span>;
    }
    const parts = [geo.district, geo.city, geo.regionName].filter(Boolean).join(', ');
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <MapPin size={12} style={{ color: 'var(--color-primary-400)', flexShrink: 0 }} />
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-primary)' }}>{parts || geo.country || ip}</span>
      </div>
    );
  };

  return (
    <AppLayout>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <PageHeader title="Registro de atividades" description="Quem usou, viu ou alterou cada credencial, quando e de onde." />

      <div className="glass" style={{ padding: 'var(--space-4)', borderRadius: 'var(--radius-lg)', marginBottom: 'var(--space-6)' }}>
        {/* A3: atalhos de período, aplicados na hora */}
        <div role="group" aria-label="Período" style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', marginBottom: 'var(--space-4)' }}>
          {ATALHOS.map(a => (
            <button key={a.id} type="button" className="botao-segmento" aria-pressed={atalho === a.id} onClick={() => aplicarAtalho(a.id)}>
              {a.rotulo}
            </button>
          ))}
        </div>
        <form onSubmit={handleFilter} style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '150px' }}>
            <label style={{ display: 'block', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-2)' }}>Usuário</label>
            <select value={filters.user_id} onChange={(e) => setFilters(prev => ({ ...prev, user_id: e.target.value }))} style={{ width: '100%', background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', padding: 'var(--space-2)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)' }}>
              <option value="">Todos</option>
              {members.map(m => (
                <option key={m.id} value={m.id}>{formatarNome(m.name) || m.email}</option>
              ))}
            </select>
          </div>
          
          <div style={{ flex: 1, minWidth: '150px' }}>
            <label style={{ display: 'block', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-2)' }}>Credencial</label>
            <select value={filters.credential_id} onChange={(e) => setFilters(prev => ({ ...prev, credential_id: e.target.value }))} style={{ width: '100%', background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', padding: 'var(--space-2)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)' }}>
              <option value="">Todas</option>
              {credentials.map(c => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
          </div>

          <div style={{ flex: 1, minWidth: '190px' }}>
            <label style={{ display: 'block', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-2)' }}>Início</label>
            <input type="datetime-local" value={filters.start_date} onChange={(e) => { setAtalho(null); setFilters(prev => ({ ...prev, start_date: e.target.value })); }} style={{ width: '100%', background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', padding: 'var(--space-2)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)' }} />
          </div>

          <div style={{ flex: 1, minWidth: '190px' }}>
            <label style={{ display: 'block', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-2)' }}>Fim</label>
            <input type="datetime-local" value={filters.end_date} onChange={(e) => { setAtalho(null); setFilters(prev => ({ ...prev, end_date: e.target.value })); }} style={{ width: '100%', background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', padding: 'var(--space-2)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)' }} />
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button type="submit" className="btn" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-2) var(--space-4)', background: 'var(--color-primary-600)', color: 'var(--color-primary-50)', borderRadius: 'var(--radius-md)', fontWeight: 'var(--font-medium)' }}>
              <Filter size={16} /> Filtrar
            </button>
            <button type="button" onClick={handleClear} className="btn" style={{ padding: 'var(--space-2) var(--space-4)', background: 'transparent', border: '1px solid var(--color-border-default)', color: 'var(--color-text-primary)', borderRadius: 'var(--radius-md)' }}>
              Limpar
            </button>
          </div>
        </form>
      </div>

      <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-lg)', overflowX: 'auto' }}>
        <table className="tabela-cartoes" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
          <thead>
            <tr style={{ background: 'var(--color-bg-elevated)', borderBottom: '1px solid var(--color-border-default)', textAlign: 'left' }}>
              <th style={{ padding: 'var(--space-4)', color: 'var(--color-text-secondary)', fontWeight: 'var(--font-medium)', fontSize: 'var(--text-sm)' }}>Data/Hora</th>
              <th style={{ padding: 'var(--space-4)', color: 'var(--color-text-secondary)', fontWeight: 'var(--font-medium)', fontSize: 'var(--text-sm)' }}>Usuário</th>
              <th style={{ padding: 'var(--space-4)', color: 'var(--color-text-secondary)', fontWeight: 'var(--font-medium)', fontSize: 'var(--text-sm)' }}>Ação</th>
              <th style={{ padding: 'var(--space-4)', color: 'var(--color-text-secondary)', fontWeight: 'var(--font-medium)', fontSize: 'var(--text-sm)' }}>Credencial</th>
              <th style={{ padding: 'var(--space-4)', color: 'var(--color-text-secondary)', fontWeight: 'var(--font-medium)', fontSize: 'var(--text-sm)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <MapPin size={13} /> Localização
                </div>
              </th>
              <th style={{ padding: 'var(--space-4)', color: 'var(--color-text-secondary)', fontWeight: 'var(--font-medium)', fontSize: 'var(--text-sm)' }}>IP</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={6}><LoadingState /></td></tr>
            ) : error ? (
              <tr><td colSpan={6}><ErrorState message={error} onRetry={() => fetchLogs(toApiFilters(filters))} /></td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={6}><EmptyState title="Nenhuma atividade no período" description="Nada foi registrado com esses filtros. Escolha 30 dias, mude a data de início ou clique em Limpar para ver todo o histórico." /></td></tr>
            ) : (
              logsDaPagina.map((log) => {
                const memberName = getMemberName(log);
                const isRemoved = !members.find(m => m.id === log.user_id);
                const dateStr = log.created_at.endsWith('Z') ? log.created_at : `${log.created_at}Z`;
                return (
                  <tr key={log.id} style={{ borderBottom: '1px solid var(--color-border-default)' }}>
                    <td style={{ padding: 'var(--space-4)', fontSize: 'var(--text-sm)', whiteSpace: 'nowrap' }}>
                      {new Date(dateStr).toLocaleString('pt-BR')}
                    </td>
                    <td style={{ padding: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>
                      <span style={{ color: isRemoved ? 'var(--color-text-muted)' : 'var(--color-primary-300)', fontStyle: isRemoved ? 'italic' : 'normal' }}>
                        {memberName}
                      </span>
                    </td>
                    <td style={{ padding: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>
                      {traduzirAcao(log.action)}
                      {detalheDaAcao(log.action, log.metadata_log) && (
                        <div
                          title={caminhoDaAcao(log.action, log.metadata_log)}
                          style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)', marginTop: '2px' }}
                        >
                          {detalheDaAcao(log.action, log.metadata_log)}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>
                      {getCredentialLabel(log.credential_id)}
                    </td>
                    <td style={{ padding: 'var(--space-4)' }}>
                      {renderLocation(log.ip_address)}
                    </td>
                    <td style={{ padding: 'var(--space-4)' }}>
                      <IPReveal ip={log.ip_address} />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        <Paginacao pagina={pagina} porPagina={LOGS_POR_PAGINA} total={logs.length} aoMudar={setPagina} />
      </div>
    </AppLayout>
  );
}
